import React, { useState, useEffect } from 'react';
import { dealerOrderCommissionsApi, dealerSalesRepsApi } from '@/services/api';
import { OrderCommission, CommissionSummary, CommissionByRep, DealerSalesRep } from '@/types';

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  earned: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  voided: 'bg-red-100 text-red-800',
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  earned: 'Earned',
  paid: 'Paid',
  voided: 'Voided',
};

export function CommissionReportsPage() {
  const [commissions, setCommissions] = useState<OrderCommission[]>([]);
  const [summary, setSummary] = useState<CommissionSummary | null>(null);
  const [byRep, setByRep] = useState<CommissionByRep[]>([]);
  const [salesReps, setSalesReps] = useState<DealerSalesRep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [salesRepFilter, setSalesRepFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCommissions, setTotalCommissions] = useState(0);
  const [pageSize] = useState(20);

  // Selected commissions for bulk actions
  const [selectedCommissions, setSelectedCommissions] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchSalesReps();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [salesRepFilter, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [salesRepFilter, statusFilter, dateFrom, dateTo, page]);

  const fetchSalesReps = async () => {
    try {
      const response = await dealerSalesRepsApi.list({ status: 'active', limit: 100 });
      setSalesReps(response?.salesReps || []);
    } catch (err: any) {
      console.error('Failed to load sales reps:', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page,
        limit: pageSize,
      };

      if (salesRepFilter !== 'all') {
        params.salesRepId = salesRepFilter;
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (dateFrom) {
        params.startDate = dateFrom;
      }

      if (dateTo) {
        params.endDate = dateTo;
      }

      // Fetch all data in parallel
      const [commissionsResponse, summaryData, byRepData] = await Promise.all([
        dealerOrderCommissionsApi.list(params),
        dealerOrderCommissionsApi.getSummary(params),
        dealerOrderCommissionsApi.getByRep(dateFrom && dateTo ? { startDate: dateFrom, endDate: dateTo } : {}),
      ]);

      setCommissions(commissionsResponse?.commissions || []);
      setTotalPages(commissionsResponse?.pagination?.pages || 1);
      setTotalCommissions(commissionsResponse?.pagination?.total || 0);
      setSummary(summaryData || null);
      setByRep(byRepData || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load commission data');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSalesRepFilter('all');
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = salesRepFilter !== 'all' || statusFilter !== 'all' || dateFrom || dateTo;

  const handleSelectAll = () => {
    const earnedCommissions = (commissions || []).filter(c => c.status === 'earned');
    if (selectedCommissions.size === earnedCommissions.length) {
      setSelectedCommissions(new Set());
    } else {
      setSelectedCommissions(new Set(earnedCommissions.map(c => c.id)));
    }
  };

  const handleSelectCommission = (id: string) => {
    const newSelected = new Set(selectedCommissions);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedCommissions(newSelected);
  };

  const handleMarkAsPaid = async () => {
    if (selectedCommissions.size === 0) {
      alert('Please select commissions to mark as paid');
      return;
    }

    const paymentReference = prompt('Enter payment reference (optional - e.g., check #, PayPal ID):');
    if (paymentReference === null) return; // User cancelled

    try {
      await dealerOrderCommissionsApi.markAsPaid({
        commissionIds: Array.from(selectedCommissions),
        paidDate: new Date().toISOString(),
        paymentReference: paymentReference || undefined,
      });

      alert(`Successfully marked ${selectedCommissions.size} commissions as paid`);
      setSelectedCommissions(new Set());
      fetchData();
    } catch (err: any) {
      alert(`Failed to mark as paid: ${err.message}`);
    }
  };

  const handleExportCsv = async () => {
    try {
      const params: any = {};

      if (salesRepFilter !== 'all') {
        params.salesRepId = salesRepFilter;
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (dateFrom) {
        params.startDate = dateFrom;
      }

      if (dateTo) {
        params.endDate = dateTo;
      }

      await dealerOrderCommissionsApi.exportCsv(params);
    } catch (err: any) {
      alert(`Failed to export: ${err.message}`);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Disclaimer Banner */}
      <div className="text-xs text-gray-500 bg-keder-dark/50 px-4 py-2 rounded border border-keder-gray/30">
        Commission tracking is a convenience tool. Keder Screens does not pay or verify commissions.
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Commission Reports</h1>
          <p className="text-gray-400 mt-1">Track and manage sales team commissions</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedCommissions.size > 0 && (
            <button
              onClick={handleMarkAsPaid}
              className="bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-2 rounded-lg transition-colors"
            >
              Mark {selectedCommissions.size} as Paid
            </button>
          )}
          <button
            onClick={handleExportCsv}
            className="bg-keder-gray hover:bg-keder-gray/80 text-white font-semibold px-6 py-2 rounded-lg transition-colors"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="card">
            <h3 className="text-sm font-medium text-gray-400 mb-1">Total This Month</h3>
            <p className="text-2xl font-bold text-white">{formatCurrency(summary.totalThisMonth)}</p>
          </div>
          <div className="card">
            <h3 className="text-sm font-medium text-gray-400 mb-1">Total YTD</h3>
            <p className="text-2xl font-bold text-white">{formatCurrency(summary.totalYTD)}</p>
          </div>
          <div className="card bg-yellow-900/10 border-yellow-800">
            <h3 className="text-sm font-medium text-yellow-400 mb-1">Pending</h3>
            <p className="text-2xl font-bold text-white">{formatCurrency(summary.pendingAmount)}</p>
            <p className="text-xs text-gray-400 mt-1">{summary.pendingCount} orders</p>
          </div>
          <div className="card bg-blue-900/10 border-blue-800">
            <h3 className="text-sm font-medium text-blue-400 mb-1">Earned</h3>
            <p className="text-2xl font-bold text-white">{formatCurrency(summary.earnedAmount)}</p>
            <p className="text-xs text-gray-400 mt-1">{summary.earnedCount} orders</p>
          </div>
          <div className="card bg-green-900/10 border-green-800">
            <h3 className="text-sm font-medium text-green-400 mb-1">Paid</h3>
            <p className="text-2xl font-bold text-white">{formatCurrency(summary.paidAmount)}</p>
            <p className="text-xs text-gray-400 mt-1">{summary.paidCount} orders</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          {/* Sales Rep Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-400">Rep:</label>
            <select
              value={salesRepFilter}
              onChange={(e) => setSalesRepFilter(e.target.value)}
              className="select-field py-2 px-3 text-sm"
            >
              <option value="all">All Reps</option>
              {salesReps.map((rep) => (
                <option key={rep.id} value={rep.id}>
                  {rep.firstName} {rep.lastName}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-400">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="select-field py-2 px-3 text-sm"
            >
              <option value="all">All Statuses</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-400">From:</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="input-field py-2 px-3 text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-400">To:</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="input-field py-2 px-3 text-sm"
            />
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-keder-yellow hover:text-yellow-400 font-medium flex items-center gap-1"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* By Rep Breakdown */}
      {(byRep || []).length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">By Rep Breakdown</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(byRep || []).map((rep) => (
              <div key={rep.salesRepId} className="p-4 bg-keder-gray/30 rounded-lg">
                <h3 className="text-white font-medium mb-2">{rep.repName}</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total:</span>
                    <span className="text-white font-medium">{formatCurrency(rep.totalCommissions)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-yellow-400">Pending:</span>
                    <span className="text-white">{formatCurrency(rep.pendingCommissions)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-400">Earned:</span>
                    <span className="text-white">{formatCurrency(rep.earnedCommissions)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-400">Paid:</span>
                    <span className="text-white">{formatCurrency(rep.paidCommissions)}</span>
                  </div>
                  <div className="flex justify-between mt-2 pt-2 border-t border-keder-gray">
                    <span className="text-gray-400">Orders:</span>
                    <span className="text-white">{rep.orderCount}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="card bg-red-900/20 border-red-800">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Results Count */}
      {!loading && (
        <div className="text-sm text-gray-400">
          Showing {(commissions || []).length} of {totalCommissions} commissions
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-keder-gray border-t-keder-yellow"></div>
          </div>
        </div>
      ) : (commissions || []).length === 0 ? (
        /* Empty State */
        <div className="card text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-white">No commissions found</h3>
          <p className="mt-1 text-sm text-gray-400">
            {hasActiveFilters ? 'Try adjusting your filters.' : 'Commissions will appear here when orders with Customer Sell Price are created.'}
          </p>
        </div>
      ) : (
        /* Commissions Table */
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-keder-gray">
              <thead className="bg-keder-gray/50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedCommissions.size > 0 && selectedCommissions.size === (commissions || []).filter(c => c.status === 'earned').length}
                      onChange={handleSelectAll}
                      className="rounded bg-keder-gray border-gray-600 text-keder-yellow focus:ring-keder-yellow"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Rep
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Customer Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-keder-gray/50">
                {(commissions || []).map((commission) => (
                  <tr key={commission.id} className="hover:bg-keder-gray/20">
                    <td className="px-6 py-4">
                      {commission.status === 'earned' && (
                        <input
                          type="checkbox"
                          checked={selectedCommissions.has(commission.id)}
                          onChange={() => handleSelectCommission(commission.id)}
                          className="rounded bg-keder-gray border-gray-600 text-keder-yellow focus:ring-keder-yellow"
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-keder-yellow font-medium">{commission.orderNumber}</div>
                        {commission.orderSidemark && (
                          <div className="text-xs text-gray-400">{commission.orderSidemark}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {commission.repName || `${commission.repFirstName} ${commission.repLastName}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {formatDate(commission.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                      {formatCurrency(commission.customerSellPrice)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {commission.commissionRate}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-keder-yellow">
                      {formatCurrency(commission.commissionAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[commission.status] || 'bg-gray-600 text-white'}`}>
                        {statusLabels[commission.status] || commission.status}
                      </span>
                      {commission.status === 'paid' && commission.paidAt && (
                        <div className="text-xs text-gray-400 mt-1">
                          {formatDate(commission.paidAt)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-400">
              Page {page} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-keder-gray text-white rounded-lg hover:bg-keder-gray/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 bg-keder-gray text-white rounded-lg hover:bg-keder-gray/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
